import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import {
  Motorcycle,
  BatteryHigh,
  CheckCircle,
  Clock,
  Wrench,
  Plus,
  QrCode,
  ListChecks,
  Storefront,
  Users,
  Buildings,
  ShieldStar,
  Gear,
  CalendarX,
  CalendarBlank,
  CaretRight,
  FilePdf,
  FileXls,
  IdentificationCard,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// ---- Export helpers ----
const exportToPDF = (title, columns, rows) => {
  Promise.all([import("jspdf"), import("jspdf-autotable")]).then(
    ([jspdfModule, autoTableModule]) => {
      const doc = new jspdfModule.jsPDF({
        orientation: rows[0]?.length > 5 ? "landscape" : "portrait",
      });
      doc.setFontSize(16);
      doc.text(title, 14, 18);
      doc.setFontSize(9);
      doc.text(
        `Gerado em: ${new Date().toLocaleDateString("pt-BR")} ${new Date().toLocaleTimeString("pt-BR")}`,
        14,
        25,
      );

      autoTableModule.default(doc, {
        head: [columns],
        body: rows,
        startY: 30,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [0, 230, 118], textColor: 0 },
      });

      doc.save(`${title.replace(/\s/g, "_")}.pdf`);
      toast.success("PDF exportado!");
    },
  );
};

const exportToExcel = (title, columns, rows) => {
  import("xlsx").then((XLSX) => {
    import("file-saver").then(({ saveAs }) => {
      const data = rows.map((row) => {
        const obj = {};
        columns.forEach((col, i) => {
          obj[col] = row[i];
        });
        return obj;
      });
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Dados");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], { type: "application/octet-stream" }),
        `${title.replace(/\s/g, "_")}.xlsx`,
      );
      toast.success("Excel exportado!");
    });
  });
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "-");

const STATUS_MAP = {
  pending: "Pendente",
  assembly: "Em Montagem",
  completed: "Finalizada",
  revision: "Em Revisão",
  revised: "Revisada",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentMotos, setRecentMotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const isSuperadmin = user?.role === "superadmin";

  // Panel states
  const [activePanel, setActivePanel] = useState(null);
  const [panelMotos, setPanelMotos] = useState([]);
  const [panelLoading, setPanelLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isSuperadmin) {
          setStats({});
        } else {
          const [statsRes, motosRes] = await Promise.all([
            axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
            axios.get(`${API}/motorcycles?limit=5`, { withCredentials: true }),
          ]);
          setStats(statsRes.data);
          setRecentMotos(motosRes.data.slice(0, 5));
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isSuperadmin]);

  const fetchPanel = async (panelName, endpoint) => {
    if (activePanel === panelName) {
      setActivePanel(null);
      return;
    }
    setActivePanel(panelName);
    setPanelLoading(true);
    try {
      const { data } = await axios.get(`${API}/${endpoint}`, {
        withCredentials: true,
      });
      setPanelMotos(data);
    } catch (err) {
      console.error(err);
      setPanelMotos([]);
    } finally {
      setPanelLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "text-[#00E676]";
      case "assembly":
        return "text-yellow-500";
      case "revision":
        return "text-orange-500";
      case "revised":
        return "text-blue-500";
      default:
        return "text-[#A1A1AA]";
    }
  };

  const getDaysInfo = (dateStr) => {
    if (!dateStr) return { text: "-", color: "text-[#A1A1AA]" };
    const diff = Math.ceil(
      (new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24),
    );
    if (diff < 0)
      return { text: `${Math.abs(diff)}d atrasada`, color: "text-red-500" };
    if (diff === 0) return { text: "Vence hoje", color: "text-orange-500" };
    if (diff <= 7) return { text: `${diff}d`, color: "text-yellow-500" };
    return { text: `${diff}d`, color: "text-[#A1A1AA]" };
  };

  // Export functions for current panel data
  const handleExport = (format) => {
    if (!panelMotos.length) return;
    const isRevisionPanel =
      activePanel === "overdue" || activePanel === "upcoming";

    const title =
      {
        assembly: "Motos em Montagem",
        completed: "Motos Finalizadas",
        revision: "Motos em Revisão",
        revised: "Motos Revisadas",
        overdue: "Revisões Vencidas",
        upcoming: "Próximas Revisões",
        all: "Todas as Motos",
      }[activePanel] || "Relatório";

    const columns = isRevisionPanel
      ? [
          "Chassi",
          "Modelo",
          "Voltagem",
          "Cliente",
          "Loja",
          "Técnico",
          "Últ. Revisão",
          "Próx. Revisão",
          "Status",
        ]
      : [
          "Chassi",
          "Modelo",
          "Voltagem",
          "Cliente",
          "Status",
          "Baterias",
          "Loja",
          "Técnico",
          "Criada em",
        ];

    const rows = panelMotos.map((m) =>
      isRevisionPanel
        ? [
            m.chassis_sn,
            m.model,
            `${m.voltage}V`,
            m.client?.name || "-",
            m.store_name || "-",
            m.technician_name || "-",
            formatDate(m.last_revision_at),
            formatDate(m.next_revision_date),
            STATUS_MAP[m.status] || m.status,
          ]
        : [
            m.chassis_sn,
            m.model,
            `${m.voltage}V`,
            m.client?.name || "-",
            STATUS_MAP[m.status] || m.status,
            `${(m.batteries || []).length}/${m.voltage === 60 ? 5 : 6}`,
            m.store_name || "-",
            m.technician_name || "-",
            formatDate(m.created_at),
          ],
    );

    if (format === "pdf") exportToPDF(title, columns, rows);
    else exportToExcel(title, columns, rows);
  };

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  if (isSuperadmin) {
    return (
      <Layout title="Painel Master">
        <div className="space-y-6 pb-4">
          <div className="card p-6 text-center">
            <ShieldStar size={48} className="text-[#00E676] mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-1">Administrador Master</h2>
            <p className="text-sm text-[#A1A1AA]">
              Gerencie empresas, licenças e acessos
            </p>
          </div>
          <Link
            to="/admin"
            className="card p-5 flex items-center gap-4 hover:bg-[#1A1A1A] transition-colors"
            data-testid="admin-companies-link"
          >
            <div className="w-14 h-14 rounded-xl bg-[#00E676] flex items-center justify-center">
              <Buildings size={32} weight="bold" className="text-black" />
            </div>
            <div>
              <p className="font-bold text-lg">Gestão de Empresas</p>
              <p className="text-sm text-[#A1A1AA]">
                Cadastrar, licenças, senhas
              </p>
            </div>
          </Link>
        </div>
      </Layout>
    );
  }

  // Helper to render moto list panel
  const renderMotoPanel = () => {
    if (!activePanel) return null;
    const isRevision = activePanel === "overdue" || activePanel === "upcoming";
    const panelTitle = {
      assembly: "Em Montagem",
      completed: "Finalizadas",
      revision: "Em Revisão",
      revised: "Revisadas",
      overdue: "Revisões Vencidas",
      upcoming: "Próximas Revisões",
      all: "Todas as Motos",
    }[activePanel];

    return (
      <div className="card p-4" data-testid="moto-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm">
            {panelTitle} ({panelMotos.length})
          </h3>
          <div className="flex items-center gap-2">
            {panelMotos.length > 0 && (
              <>
                <button
                  onClick={() => handleExport("pdf")}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20"
                  data-testid="export-pdf-btn"
                >
                  <FilePdf size={14} /> PDF
                </button>
                <button
                  onClick={() => handleExport("excel")}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20"
                  data-testid="export-excel-btn"
                >
                  <FileXls size={14} /> Excel
                </button>
              </>
            )}
            <button
              onClick={() => setActivePanel(null)}
              className="text-xs text-[#A1A1AA] hover:text-white ml-1"
            >
              Fechar
            </button>
          </div>
        </div>

        {panelLoading ? (
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin w-6 h-6 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
          </div>
        ) : panelMotos.length === 0 ? (
          <p className="text-sm text-[#A1A1AA] text-center py-4">
            Nenhuma moto encontrada
          </p>
        ) : (
          <div className="space-y-2">
            {panelMotos.map((moto) => {
              const daysInfo = isRevision
                ? getDaysInfo(moto.next_revision_date)
                : null;
              return (
                <button
                  key={moto.id}
                  onClick={() =>
                    navigate(
                      isRevision ? `/revisao/${moto.id}` : `/motos/${moto.id}`,
                    )
                  }
                  className="w-full bg-[#1A1A1A] rounded-lg p-3 flex items-center justify-between hover:bg-[#222] transition-colors text-left"
                  data-testid={`panel-moto-${moto.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-sm font-medium truncate">
                        {moto.chassis_sn}
                      </p>
                      {isRevision && daysInfo && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            activePanel === "overdue"
                              ? "bg-red-500/10 text-red-500"
                              : "bg-yellow-500/10 text-yellow-500"
                          }`}
                        >
                          {daysInfo.text}
                        </span>
                      )}
                      {!isRevision && (
                        <span
                          className={`text-xs ${getStatusColor(moto.status)}`}
                        >
                          {STATUS_MAP[moto.status]}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-3 mt-1">
                      <p className="text-xs text-[#A1A1AA]">
                        {moto.model} - {moto.voltage}V
                      </p>
                      {moto.store_name && (
                        <p className="text-xs text-[#A1A1AA]">
                          {moto.store_name}
                        </p>
                      )}
                    </div>
                    {isRevision && moto.next_revision_date && (
                      <p className="text-xs text-[#A1A1AA] mt-1">
                        Vencimento: {formatDate(moto.next_revision_date)}
                      </p>
                    )}
                  </div>
                  <CaretRight
                    size={18}
                    className="text-[#A1A1AA] ml-2 flex-shrink-0"
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout title="Dashboard">
      <div className="space-y-6 pb-4">
        {/* Revision Alerts */}
        {(stats?.revision_overdue > 0 || stats?.revision_upcoming > 0) && (
          <div className="space-y-3">
            <h2 className="text-lg font-bold">Revisões</h2>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() =>
                  fetchPanel("overdue", "revision-status?status_filter=overdue")
                }
                className={`card p-4 text-left transition-colors ${activePanel === "overdue" ? "border-red-500/50 bg-red-500/5" : "hover:bg-[#1A1A1A]"}`}
                data-testid="revision-overdue-card"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <CalendarX size={24} className="text-red-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-500">
                      {stats?.revision_overdue || 0}
                    </p>
                    <p className="text-xs text-red-400">Vencidas</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() =>
                  fetchPanel(
                    "upcoming",
                    "revision-status?status_filter=upcoming",
                  )
                }
                className={`card p-4 text-left transition-colors ${activePanel === "upcoming" ? "border-yellow-500/50 bg-yellow-500/5" : "hover:bg-[#1A1A1A]"}`}
                data-testid="revision-upcoming-card"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                    <CalendarBlank size={24} className="text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-500">
                      {stats?.revision_upcoming || 0}
                    </p>
                    <p className="text-xs text-yellow-400">Próximas</p>
                  </div>
                </div>
              </button>
            </div>
            {(activePanel === "overdue" || activePanel === "upcoming") &&
              renderMotoPanel()}
          </div>
        )}

        {/* Status Cards */}
        <div className="space-y-3">
          <h2 className="text-lg font-bold">Montagem e Status</h2>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => fetchPanel("all", "motorcycles")}
              className={`card p-4 text-left transition-colors ${activePanel === "all" ? "border-[#00E676]/50 bg-[#00E676]/5" : "hover:bg-[#1A1A1A]"}`}
              data-testid="stat-total-motorcycles"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
                  <Motorcycle size={24} className="text-[#00E676]" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {stats?.total_motorcycles || 0}
                  </p>
                  <p className="text-xs text-[#A1A1AA]">Total Motos</p>
                </div>
              </div>
            </button>

            <button
              onClick={() =>
                fetchPanel("completed", "motorcycles?status=completed")
              }
              className={`card p-4 text-left transition-colors ${activePanel === "completed" ? "border-[#00E676]/50 bg-[#00E676]/5" : "hover:bg-[#1A1A1A]"}`}
              data-testid="stat-completed"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
                  <CheckCircle size={24} className="text-[#00E676]" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {stats?.completed_motorcycles || 0}
                  </p>
                  <p className="text-xs text-[#A1A1AA]">Finalizadas</p>
                </div>
              </div>
            </button>

            <button
              onClick={() =>
                fetchPanel("assembly", "motorcycles?status=assembly")
              }
              className={`card p-4 text-left transition-colors ${activePanel === "assembly" ? "border-yellow-500/50 bg-yellow-500/5" : "hover:bg-[#1A1A1A]"}`}
              data-testid="stat-in-assembly"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <Wrench size={24} className="text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {stats?.in_assembly || 0}
                  </p>
                  <p className="text-xs text-[#A1A1AA]">Em Montagem</p>
                </div>
              </div>
            </button>

            <button
              onClick={() =>
                fetchPanel("revision", "motorcycles?status=revision")
              }
              className={`card p-4 text-left transition-colors ${activePanel === "revision" ? "border-orange-500/50 bg-orange-500/5" : "hover:bg-[#1A1A1A]"}`}
              data-testid="stat-in-revision"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                  <Wrench size={24} className="text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {stats?.in_revision || 0}
                  </p>
                  <p className="text-xs text-[#A1A1AA]">Em Revisão</p>
                </div>
              </div>
            </button>

            <button
              onClick={() =>
                fetchPanel("revised", "motorcycles?status=revised")
              }
              className={`card p-4 text-left transition-colors ${activePanel === "revised" ? "border-blue-500/50 bg-blue-500/5" : "hover:bg-[#1A1A1A]"}`}
              data-testid="stat-revised"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <CheckCircle size={24} className="text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.revised || 0}</p>
                  <p className="text-xs text-[#A1A1AA]">Revisadas</p>
                </div>
              </div>
            </button>

            <div className="card p-4" data-testid="stat-batteries">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <BatteryHigh size={24} className="text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {stats?.total_batteries_linked || 0}
                  </p>
                  <p className="text-xs text-[#A1A1AA]">Baterias</p>
                </div>
              </div>
            </div>
          </div>

          {/* Panel for status cards */}
          {activePanel &&
            activePanel !== "overdue" &&
            activePanel !== "upcoming" &&
            renderMotoPanel()}
        </div>

        {/* Quick Actions */}
        <div className="space-y-3">
          <h2 className="text-lg font-bold">Ações Rápidas</h2>
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/nova-moto"
              className="card p-4 flex flex-col items-center gap-2 hover:bg-[#1A1A1A] transition-colors"
              data-testid="quick-action-new-moto"
            >
              <div className="w-12 h-12 rounded-full bg-[#00E676] flex items-center justify-center">
                <Plus size={28} weight="bold" className="text-black" />
              </div>
              <span className="text-sm font-medium">Nova Moto</span>
            </Link>
            <Link
              to="/consulta"
              className="card p-4 flex flex-col items-center gap-2 hover:bg-[#1A1A1A] transition-colors"
              data-testid="quick-action-search"
            >
              <div className="w-12 h-12 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                <QrCode size={28} className="text-white" />
              </div>
              <span className="text-sm font-medium">Consultar</span>
            </Link>
          </div>
        </div>

        {/* Cadastros */}
        <div className="space-y-3">
          <h2 className="text-lg font-bold">Cadastros</h2>
          <div className="grid grid-cols-3 gap-3">
            <Link
              to="/lojas"
              className="card p-4 flex flex-col items-center gap-2 hover:bg-[#1A1A1A] transition-colors"
              data-testid="quick-action-stores"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Storefront size={24} className="text-purple-500" />
              </div>
              <span className="text-xs font-medium">Lojas</span>
            </Link>
            <Link
              to="/tecnicos"
              className="card p-4 flex flex-col items-center gap-2 hover:bg-[#1A1A1A] transition-colors"
              data-testid="quick-action-technicians"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Users size={24} className="text-blue-500" />
              </div>
              <span className="text-xs font-medium">Técnicos</span>
            </Link>
            <Link
              to="/checklists"
              className="card p-4 flex flex-col items-center gap-2 hover:bg-[#1A1A1A] transition-colors"
              data-testid="quick-action-checklists"
            >
              <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
                <ListChecks size={24} className="text-[#00E676]" />
              </div>
              <span className="text-xs font-medium">Checklists</span>
            </Link>
            <Link
              to="/clientes"
              className="card p-4 flex flex-col items-center gap-2 hover:bg-[#1A1A1A] transition-colors"
              data-testid="quick-action-clients"
            >
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <IdentificationCard size={24} className="text-orange-500" />
              </div>
              <span className="text-xs font-medium">Clientes</span>
            </Link>
            <Link
              to="/configuracoes"
              className="card p-4 flex flex-col items-center gap-2 hover:bg-[#1A1A1A] transition-colors"
              data-testid="quick-action-settings"
            >
              <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <Gear size={24} className="text-yellow-500" />
              </div>
              <span className="text-xs font-medium">Config.</span>
            </Link>
          </div>
        </div>

        {/* Today Stats */}
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <Clock size={24} className="text-[#A1A1AA]" />
            <div>
              <p className="text-sm text-[#A1A1AA]">Motos cadastradas hoje</p>
              <p className="text-xl font-bold">
                {stats?.motorcycles_today || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
